import { PRODUCT_INFO } from '@hsc/common/constants/product-info';

/*
 * Use naming convention of <item>_<condition>
 */
export type NotificationData = {
  title: string;
  message: string;
  type: 'warning' | 'error' | 'info';
};

const CHECK_NUMBER_HINT = 'Please check that the number is entered correctly.';

export const CUSTOM_VALIDATION_ERROR_MESSAGES = {
  addressNotFound: 'Address not found, please try again.',
  minimumSquareFeet: 'Minimum value is 1',
} as const;

export const USER_NOTIFICATIONS = {
  agencyNotPilotEligible: {
    title: 'Not currently available',
    message: `Contact your territory manager to learn more about the Nationwide ${PRODUCT_INFO.name}.`,
    type: 'error',
  },
  agentAgencyMismatch: (isPolicy: boolean) => ({
    title: `${isPolicy ? 'Policy' : 'Submission'} not available`,
    message: `This ${isPolicy ? 'policy' : 'submission'} is not associated with your agency code. ${CHECK_NUMBER_HINT}`,
    type: 'error',
  }),
  axiomNotEligible: {
    title: 'Home ineligible',
    message: `Not sure what happened but the home is ineligible for our ${PRODUCT_INFO.name} coverage.`,
    type: 'error',
  },
  equipmentBreakdownDoubleCheck: {
    title: 'Did you remove Equipment Breakdown from the policy?',
    message: 'If not, please remove it now. If yes, click Continue below.',
    type: 'warning',
  },
  equipmentBreakdownNotEligible: {
    title: `${PRODUCT_INFO.name} not available`,
    message: `This policy has the Equipment Breakdown endorsement, which is not available with the Nationwide ${PRODUCT_INFO.name}. You can proceed with a quote, but must remove Equipment Breakdown to purchase.`,
    type: 'warning',
  },
  mailingAddressNotValid: {
    title: 'Mailing address not valid',
    message: 'The mailing address provided is not valid or matches the property address.',
    type: 'error',
  },
  mobileOrManufacturedNotEligible: {
    title: "We can't cover your home",
    message: `Since your home is considered a mobile home or a manufactured home not on a permanent foundation, it's ineligible for our ${PRODUCT_INFO.name} coverage.`,
    type: 'error',
  },
  pilotStateNotEligible: {
    title: `${PRODUCT_INFO.name} not available`,
    message: `We don't offer a ${PRODUCT_INFO.name} in this state at this time.`,
    type: 'error',
  },
  policyNotActive: {
    title: 'Policy not active',
    message: `The policy is no longer active. ${CHECK_NUMBER_HINT}`,
    type: 'error',
  },
  policyNotFound: {
    title: `Policy not found`,
    message: CHECK_NUMBER_HINT,
    type: 'error',
  },
  policyNotHomeowners: {
    title: 'Policy not for homeowners insurance',
    message: `The policy number entered is not for a homeowners insurance policy. ${CHECK_NUMBER_HINT}`,
    type: 'error',
  },
  propertySquareFootageNotEligible: {
    title: "We can't cover your home",
    message: `Homes larger than 5000 ft² are ineligible for our ${PRODUCT_INFO.name} coverage.`,
    type: 'error',
  },
  registeredHistoricNotEligible: {
    title: "We can't cover your home",
    message: `Since your home is listed on a historical registry, it's ineligible for our ${PRODUCT_INFO.name} coverage.`,
    type: 'error',
  },
  submissionNotActive: {
    title: 'Submission not active',
    message: `The submission is no longer active. ${CHECK_NUMBER_HINT}`,
    type: 'error',
  },
  submissionNotFound: {
    title: 'Submission not found',
    message: CHECK_NUMBER_HINT,
    type: 'error',
  },
  systemError: {
    title: 'System error',
    message: 'There was an unexpected system error. Please try again later.',
    type: 'error',
  },
  townhouseNotEligible: {
    title: "We can't cover your home",
    message: `Homes that are considered townhouses are ineligible for our ${PRODUCT_INFO.name} coverage.`,
    type: 'error',
  },
} satisfies Record<string, NotificationData | ((...args: never[]) => NotificationData)>;
